import DOMPurify from 'dompurify';

/** Fonts the editor offers; the server allows exactly these. */
export const NOTIFICATION_FONTS = [
    'Arial',
    'Georgia',
    'Times New Roman',
    'Courier New',
    'Verdana',
    'Trebuchet MS',
    'Tahoma',
] as const;

/** Font sizes the editor offers; the server allows exactly these. */
export const NOTIFICATION_FONT_SIZES = [
    { value: '12px', label: 'Small (12)' },
    { value: '14px', label: 'Normal (14)' },
    { value: '16px', label: 'Medium (16)' },
    { value: '18px', label: 'Large (18)' },
    { value: '24px', label: 'X-Large (24)' },
    { value: '32px', label: 'Huge (32)' },
] as const;

export const NOTIFICATION_ALIGNMENTS = ['left', 'center', 'right', 'justify'] as const;

export const NOTIFICATION_ALLOWED_TAGS = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'span', 'a', 'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'blockquote', 'img', 'div',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col',
];

export const NOTIFICATION_ALLOWED_ATTR = [
    'href', 'target', 'rel', 'src', 'alt', 'style', 'data-cf-video', 'colspan', 'rowspan',
];

const ALIGN_TAGS = ['p', 'h1', 'h2', 'h3', 'li', 'td', 'th'];
const SPAN_LIMIT_REGEX = /^(?:[1-9]|1\d|20)$/;

/** Parses "a: b; c: d" into lowercase property -> trimmed value pairs. */
function parseStyle(style: string): Array<[string, string]> {
    return style
        .split(';')
        .map(d => d.trim())
        .filter(Boolean)
        .map(d => {
            const i = d.indexOf(':');
            return i < 0 ? ['', ''] : [d.slice(0, i).trim().toLowerCase(), d.slice(i + 1).trim()];
        }) as Array<[string, string]>;
}

/** Rebuilds a safe style attribute for the tag, or null when nothing survives. */
export function sanitizeStyle(tag: string, style: string): string | null {
    const decls = parseStyle(style);
    const out: string[] = [];
    if (tag === 'span') {
        const fam = decls.find(([k]) => k === 'font-family');
        const font = fam ? parseAllowedFontFamily(`font-family: ${fam[1]}`) : null;
        if (font) out.push(`font-family: ${font}`);
        const size = decls.find(([k]) => k === 'font-size');
        const sz = size ? NOTIFICATION_FONT_SIZES.find(f => f.value === size[1].toLowerCase()) : null;
        if (sz) out.push(`font-size: ${sz.value}`);
    } else if (ALIGN_TAGS.includes(tag)) {
        const al = decls.find(([k]) => k === 'text-align');
        const a = al ? NOTIFICATION_ALIGNMENTS.find(x => x === al[1].toLowerCase()) : null;
        if (a) out.push(`text-align: ${a}`);
    }
    return out.length ? out.join('; ') : null;
}

const VIDEO_UID_REGEX = /^[0-9a-f]{32}$/i;
const LINK_HREF_REGEX = /^(https?:|mailto:)/i;
const IMG_SRC_REGEX = /^https:\/\//i;

/** Returns the allowlisted font name from an inline style, or null. */
export function parseAllowedFontFamily(style: string): string | null {
    const m = /^\s*font-family\s*:\s*["']?([^;"']+?)["']?\s*;?\s*$/i.exec(style);
    if (!m) return null;
    const name = m[1].trim();
    return NOTIFICATION_FONTS.find(f => f.toLowerCase() === name.toLowerCase()) ?? null;
}

let purifier: ReturnType<typeof DOMPurify> | null = null;

function getPurifier() {
    if (purifier) return purifier;
    const p = DOMPurify(window);
    p.addHook('uponSanitizeAttribute', (node, data) => {
        const tag = node.nodeName.toLowerCase();
        const name = data.attrName;
        const value = data.attrValue;
        let ok = true;
        let handled = true;
        if (name === 'style') {
            const clean = sanitizeStyle(tag, value);
            if (clean) {
                data.attrValue = clean;
                node.setAttribute('style', clean);
            } else {
                ok = false;
            }
        } else if (name === 'colspan' || name === 'rowspan') {
            ok = (tag === 'td' || tag === 'th') && SPAN_LIMIT_REGEX.test(value.trim());
        } else if (name === 'href') {
            ok = tag === 'a' && LINK_HREF_REGEX.test(value.trim());
        } else if (name === 'src') {
            ok = tag === 'img' && IMG_SRC_REGEX.test(value.trim());
        } else if (name === 'data-cf-video') {
            ok = tag === 'div' && VIDEO_UID_REGEX.test(value);
        } else if (name === 'target' || name === 'rel') {
            ok = tag === 'a';
        } else if (name === 'alt') {
            ok = tag === 'img';
        } else {
            handled = false;
        }
        if (!handled) return;
        if (!ok) {
            data.keepAttr = false;
        } else {
            data.forceKeepAttr = true;
        }
    });
    p.addHook('afterSanitizeAttributes', node => {
        if (node.nodeName.toLowerCase() === 'a' && node.hasAttribute('href')) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });
    purifier = p;
    return p;
}

/** Sanitizes notification HTML with the same allowlist the server enforces. */
export function sanitizeNotificationHtml(html: string): string {
    return getPurifier().sanitize(html, {
        ALLOWED_TAGS: NOTIFICATION_ALLOWED_TAGS,
        ALLOWED_ATTR: NOTIFICATION_ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false,
    }) as string;
}

const ENTITIES: Record<string, string> = {
    '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ',
};

/** Plain text preview of notification HTML: images -> [image], videos -> [video]. */
export function htmlToPlainText(html: string): string {
    if (!html) return '';
    return html
        .replace(/<div\b[^>]*data-cf-video=[^>]*>\s*<\/div>/gi, ' [video] ')
        .replace(/<img\b[^>]*>/gi, ' [image] ')
        .replace(/<\/t[dh]>(?=\s*<t[dh])/gi, ' | ')
        .replace(/<\/tr>(?=\s*(?:<\/?(?:tbody|thead|tfoot)[^>]*>\s*)*<tr)/gi, ' / ')
        .replace(/<\/(p|div|li|h[1-6]|blockquote|t[dh]|tr|table)>|<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, m => ENTITIES[m] ?? m)
        .replace(/\s+/g, ' ')
        .trim();
}

/** True when html has visible text or media. */
export function htmlHasContent(html: string): boolean {
    return htmlToPlainText(html).length > 0;
}
